/** Utilitários de geração e formatação do caça-palavras */

export { formatGameClock, formatGameTime, getElapsedMs, normalizeGameTimeMs } from '../../utils/marketingGameTime'

/** 1º/2º/3º com medalha; demais posições em número. */
export function rankingPlaceLabel(place: number): string {
  if (place === 1) return '🥇'
  if (place === 2) return '🥈'
  if (place === 3) return '🥉'
  return String(place)
}

export const BOARD_SIZE = 15

export const FILLER_LETTERS = 'ABCDEFGHIJLMNOPQRSTUVXZ'

/** Somente direções "para frente" */
const DIRECTIONS_BY_ORIENTATION: Record<'H' | 'V' | 'D', Array<[number, number]>> = {
  H: [[0, 1]],
  V: [[1, 0]],
  D: [
    [1, 1],
    [1, -1],
  ],
}

const ORIENTATION_RATIO = { H: 0.6, V: 0.3, D: 0.1 }

export function normalizePuzzleWord(word: string): string {
  return (word || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z]/g, '')
    .toUpperCase()
}

function shuffleArray<T>(arr: T[]): T[] {
  const a = arr.slice()
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    const tmp = a[i]
    a[i] = a[j]
    a[j] = tmp
  }
  return a
}

function buildEmptyGrid(): (string | null)[][] {
  return Array.from({ length: BOARD_SIZE }, () => Array(BOARD_SIZE).fill(null))
}

function buildOrientationAssignment(words: string[]): Record<string, 'H' | 'V' | 'D'> {
  const n = words.length
  const countH = Math.round(n * ORIENTATION_RATIO.H)
  const countV = Math.round(n * ORIENTATION_RATIO.V)
  const countD = n - countH - countV
  const pool: Array<'H' | 'V' | 'D'> = [
    ...Array(countH).fill('H'),
    ...Array(countV).fill('V'),
    ...Array(countD).fill('D'),
  ]
  const shuffled = shuffleArray(pool)
  const assignment: Record<string, 'H' | 'V' | 'D'> = {}
  words.forEach((w, idx) => {
    assignment[w] = shuffled[idx] ?? 'H'
  })
  return assignment
}

function tryPlaceWord(
  grid: (string | null)[][],
  word: string,
  allowedDirs: Array<[number, number]>
): Array<[number, number]> | null {
  for (let attempt = 0; attempt < 800; attempt++) {
    const dir = allowedDirs[Math.floor(Math.random() * allowedDirs.length)]
    const r0 = Math.floor(Math.random() * BOARD_SIZE)
    const c0 = Math.floor(Math.random() * BOARD_SIZE)
    const len = word.length
    const rEnd = r0 + dir[0] * (len - 1)
    const cEnd = c0 + dir[1] * (len - 1)
    if (rEnd < 0 || rEnd >= BOARD_SIZE || cEnd < 0 || cEnd >= BOARD_SIZE) continue

    const cells: Array<[number, number]> = []
    let ok = true
    for (let i = 0; i < len; i++) {
      const r = r0 + dir[0] * i
      const c = c0 + dir[1] * i
      const existing = grid[r][c]
      if (existing !== null && existing !== word[i]) {
        ok = false
        break
      }
      cells.push([r, c])
    }
    if (!ok) continue

    for (let j = 0; j < len; j++) {
      grid[cells[j][0]][cells[j][1]] = word[j]
    }
    return cells
  }
  return null
}

export interface PuzzleResult {
  grid: string[][]
  placements: Record<string, Array<[number, number]>>
}

function attemptGeneratePuzzle(words: string[]): PuzzleResult | null {
  const grid = buildEmptyGrid()
  const orientationOf = buildOrientationAssignment(words)
  const sorted = words.slice().sort((a, b) => b.length - a.length)
  const placements: Record<string, Array<[number, number]>> = {}

  for (const w of sorted) {
    const dirs = DIRECTIONS_BY_ORIENTATION[orientationOf[w]]
    const cells = tryPlaceWord(grid, w, dirs)
    if (!cells) return null
    placements[w] = cells
  }

  const filled: string[][] = grid.map((row) =>
    row.map((cell) =>
      cell === null
        ? FILLER_LETTERS[Math.floor(Math.random() * FILLER_LETTERS.length)]
        : cell
    )
  )

  return { grid: filled, placements }
}

export function generatePuzzle(rawWords: string[]): PuzzleResult {
  const words = rawWords.map(normalizePuzzleWord).filter((w) => w.length >= 2)
  for (let attempt = 0; attempt < 200; attempt++) {
    const result = attemptGeneratePuzzle(words)
    if (result) return result
  }
  const fallback = attemptGeneratePuzzle(words)
  return fallback ?? { grid: buildEmptyGrid().map((row) => row.map(() => 'A')), placements: {} }
}

export function computePath(
  r0: number,
  c0: number,
  r1: number,
  c1: number
): Array<[number, number]> | null {
  const dr = r1 - r0
  const dc = c1 - c0
  const isStraight = dr === 0 || dc === 0 || Math.abs(dr) === Math.abs(dc)
  if (!isStraight) return null
  const steps = Math.max(Math.abs(dr), Math.abs(dc))
  const stepR = steps === 0 ? 0 : dr / steps
  const stepC = steps === 0 ? 0 : dc / steps
  const path: Array<[number, number]> = []
  for (let i = 0; i <= steps; i++) {
    path.push([r0 + stepR * i, c0 + stepC * i])
  }
  return path
}

const KEYCAP_EMOJIS = ['1️⃣', '2️⃣', '3️⃣', '4️⃣', '5️⃣', '6️⃣', '7️⃣', '8️⃣', '9️⃣', '🔟', '⑪', '⑫']

export function buildOrderSquares(order: number[]): string {
  return (order || [])
    .map((n) => KEYCAP_EMOJIS[n - 1] || `${n}`)
    .join('')
}

export function formatDateBr(isoDate: string): string {
  if (!isoDate) return '—'
  const [y, m, d] = isoDate.slice(0, 10).split('-')
  if (!y || !m || !d) return isoDate
  return `${d}/${m}/${y}`
}
